import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, ShieldAlert, EyeOff, Eye, Trash2, AlertTriangle, CheckCircle2, ShieldCheck,
  StickyNote, Send, UserMinus, Ban,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { PersonLink } from "@/components/groups/PersonLink";
import api from "@/lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

const GROUP_ACTION_LABEL = {
  hide: "Hidden pending review",
  soft_delete: "Deleted (recoverable)",
  remove: "Removed by group moderation",
};

const Section = ({ title, children, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">{title}</div>
    {children}
  </div>
);

/** Phase 7 — the Group report DETAIL. Every content action, the member actions, the
 * Group-private notes, the neutral author notice and the escalation to Startup Otter are all
 * reachable here, so an organizer never has to leave the report to moderate the content.
 * The payload is server-allowlisted: no reporter identity and no platform note can arrive. */
export function GroupReportDetail({ groupId, reportId, onClose }) {
  const qc = useQueryClient();
  const [note, setNote] = React.useState("");
  const [authorMsg, setAuthorMsg] = React.useState("");
  const [escalateReason, setEscalateReason] = React.useState("");

  const detail = useQuery({
    queryKey: ["group-report", groupId, reportId],
    queryFn: async () => (await api.get(`/groups/${groupId}/moderation/reports/${reportId}`)).data,
    enabled: Boolean(reportId),
    retry: false,
  });
  const r = detail.data;

  // A deep link can outlive the role that earned it, or point at a report this organizer may
  // not review. The server answers 403/404; the dialog says so plainly instead of spinning.
  React.useEffect(() => {
    if (!detail.isError) return;
    toast.error(detail.error?.response?.data?.detail
                || "That report isn't available to you.");
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.isError]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["group-report", groupId, reportId] });
    qc.invalidateQueries({ queryKey: ["group-moderation", groupId] });
    qc.invalidateQueries({ queryKey: ["group-moderation-counts", groupId] });
    qc.invalidateQueries({ queryKey: ["group-moderation-history", groupId] });
  };

  const act = useMutation({
    mutationFn: async ({ action, body }) =>
      (await api.post(`/groups/${groupId}/moderation/reports/${reportId}/${action}`, body)).data,
    onSuccess: (_d, v) => { toast.success(v.toast || "Done."); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't work."),
  });

  const member = useMutation({
    mutationFn: async ({ verb, userId }) =>
      (await api.post(`/groups/${groupId}/members/${userId}/${verb}`, { reason: "" })).data,
    onSuccess: (_d, v) => { toast.success(v.verb === "ban" ? "Member banned." : "Member removed."); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't work."),
  });

  const decide = (action, toastMsg) =>
    act.mutate({ action: "action", body: { action, note: note.trim() }, toast: toastMsg });

  const busy = act.isPending || member.isPending;
  const authorId = r?.author?.user_id;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="group-report-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" /> Review report
          </DialogTitle>
          <DialogDescription>{r?.reason_label || "Loading…"}</DialogDescription>
        </DialogHeader>

        {!r ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">{r.content_type}</Badge>
              <span>Reported {fmt(r.created_at)}</span>
              <Badge variant="outline" data-testid="group-report-detail-status">{r.review_status}</Badge>
              {r.platform_held ? (
                <Badge variant="destructive" className="gap-1" data-testid="group-report-detail-platform">
                  <ShieldAlert className="h-3 w-3" /> Held by Startup Otter
                </Badge>
              ) : null}
              {r.escalated ? (
                <Badge className="gap-1" data-testid="group-report-detail-escalated">
                  <ShieldCheck className="h-3 w-3" /> Referred to Startup Otter
                </Badge>
              ) : null}
            </div>

            {r.details ? (
              <div className="rounded-lg bg-secondary/60 px-3 py-2 text-sm" data-testid="group-report-detail-reporter-note">
                <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Reporter note</span>
                <p className="mt-1 break-words">{r.details}</p>
              </div>
            ) : null}

            <div className="rounded-lg border border-border p-3">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">
                  Reported content
                </span>
                <Badge variant={r.content_removed ? "secondary" : "outline"}>
                  {r.group_action ? GROUP_ACTION_LABEL[r.group_action] || r.content_status
                    : (r.content_status || "active").replace(/_/g, " ")}
                </Badge>
              </div>
              {r.author ? (
                <div className="mb-2 text-sm"><PersonLink person={r.author} testId="group-report-detail-author" /></div>
              ) : null}
              {!r.content_exists ? (
                <p className="text-sm italic text-muted-foreground">This content no longer exists.</p>
              ) : (
                <div data-testid="group-report-detail-body">
                  {r.content_body && r.content_body !== "<p></p>"
                    ? <RichTextView html={r.content_body} />
                    : <p className="text-sm italic text-muted-foreground">No text.</p>}
                </div>
              )}
            </div>

            {/* Content actions — Group-scoped equivalents of the platform actions. */}
            <Section title="Content actions" testId="group-report-detail-actions">
              {r.platform_held ? (
                <p className="text-sm text-muted-foreground" data-testid="group-report-detail-platform-note">
                  Startup Otter has acted on this content. Group organizers can&apos;t change or
                  restore a platform decision.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {!r.content_removed && r.content_exists ? (
                    <>
                      <Button size="sm" variant="outline" className="justify-start" disabled={busy}
                              data-testid="group-report-action-hide"
                              onClick={() => decide("hide", "Hidden pending review.")}>
                        <EyeOff className="mr-2 h-4 w-4" /> Hide pending review
                      </Button>
                      <Button size="sm" variant="outline" className="justify-start" disabled={busy}
                              data-testid="group-report-action-soft_delete"
                              onClick={() => decide("soft_delete", "Deleted — recoverable.")}>
                        <Trash2 className="mr-2 h-4 w-4" /> Soft-delete
                      </Button>
                      <Button size="sm" variant="destructive" className="justify-start sm:col-span-2"
                              disabled={busy} data-testid="group-report-action-remove"
                              onClick={() => decide("remove", "Removed from the group.")}>
                        <AlertTriangle className="mr-2 h-4 w-4" /> Remove from group
                      </Button>
                    </>
                  ) : null}
                  {r.content_removed && r.content_exists ? (
                    <Button size="sm" variant="outline" className="justify-start sm:col-span-2"
                            disabled={busy} data-testid="group-report-action-restore"
                            onClick={() => decide("restore", "Content restored.")}>
                      <Eye className="mr-2 h-4 w-4" />
                      {r.group_action === "hide" ? "Unhide" : "Restore"}
                    </Button>
                  ) : null}
                </div>
              )}
            </Section>

            <Section title="Report decision">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button size="sm" variant="secondary" className="justify-start" disabled={busy}
                        data-testid="group-report-action-dismiss"
                        onClick={() => decide("dismiss", "Report dismissed.")}>
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Dismiss report
                </Button>
                <Button size="sm" variant="secondary" className="justify-start" disabled={busy}
                        data-testid="group-report-action-reviewed"
                        onClick={() => decide("reviewed", "Marked reviewed.")}>
                  <ShieldCheck className="mr-2 h-4 w-4" /> Mark reviewed
                </Button>
              </div>
            </Section>

            {/* Member moderation stays a SEPARATE action from content moderation. */}
            {authorId ? (
              <Section title="Member actions">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Button size="sm" variant="outline" className="justify-start" disabled={busy}
                          data-testid="group-report-member-remove"
                          onClick={() => member.mutate({ verb: "remove", userId: authorId })}>
                    <UserMinus className="mr-2 h-4 w-4" /> Remove member
                  </Button>
                  <Button size="sm" variant="outline" className="justify-start" disabled={busy}
                          data-testid="group-report-member-ban"
                          onClick={() => member.mutate({ verb: "ban", userId: authorId })}>
                    <Ban className="mr-2 h-4 w-4" /> Ban member
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Removing a member doesn&apos;t remove their content, and removing content
                  doesn&apos;t remove the member.
                </p>
              </Section>
            ) : null}

            <Section title="Group notes">
              {(r.notes || []).length === 0 ? (
                <p className="text-sm italic text-muted-foreground" data-testid="group-report-notes-empty">
                  No notes yet.
                </p>
              ) : (
                <div className="space-y-2" data-testid="group-report-notes">
                  {r.notes.map((n) => (
                    <div key={n.id} className="rounded-lg bg-secondary/50 px-3 py-2" data-testid={`group-report-note-${n.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-[560]">{n.author?.display_name || "Organizer"}</span>
                        <Badge variant="outline">{n.kind === "to_author" ? "Sent to author" : "Group only"}</Badge>
                      </div>
                      <p className="mt-1 break-words text-sm">{n.text}</p>
                      <span className="text-xs text-muted-foreground">{fmt(n.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-lg border border-border p-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                          placeholder="Note for this group's organizers only…"
                          data-testid="group-report-internal-note" />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="secondary" disabled={!note.trim() || busy}
                          data-testid="group-report-add-note"
                          onClick={() => act.mutate({ action: "notes", body: { text: note.trim(), kind: "internal" }, toast: "Note added." },
                                                    { onSuccess: () => setNote("") })}>
                    <StickyNote className="mr-2 h-4 w-4" /> Add group note
                  </Button>
                </div>
              </div>
              <div className="rounded-lg border border-border p-2">
                <Textarea value={authorMsg} onChange={(e) => setAuthorMsg(e.target.value)} rows={2}
                          placeholder="Message to the author from the organizers…"
                          data-testid="group-report-author-message" />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="secondary" disabled={!authorMsg.trim() || busy}
                          data-testid="group-report-send-author"
                          onClick={() => act.mutate({ action: "notes", body: { text: authorMsg.trim(), kind: "to_author" }, toast: "Notice sent to the author." },
                                                    { onSuccess: () => setAuthorMsg("") })}>
                    <Send className="mr-2 h-4 w-4" /> Send notice
                  </Button>
                </div>
              </div>
            </Section>

            <Section title="Escalate to Startup Otter">
              {r.escalated ? (
                <p className="text-sm text-muted-foreground" data-testid="group-report-escalated-note">
                  This report has been referred to Startup Otter. Their review is independent and
                  organizers can&apos;t see or close it.
                </p>
              ) : (
                <div className="rounded-lg border border-border p-2">
                  <Textarea value={escalateReason} onChange={(e) => setEscalateReason(e.target.value)} rows={2}
                            placeholder="Why does this exceed group-level authority? (optional)"
                            data-testid="group-report-escalate-reason" />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" disabled={busy} data-testid="group-report-escalate"
                            onClick={() => act.mutate({ action: "escalate", body: { reason: escalateReason.trim() }, toast: "Referred to Startup Otter." })}>
                      <ShieldCheck className="mr-2 h-4 w-4" /> Escalate to Startup Otter
                    </Button>
                  </div>
                </div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
